/**
 * The Add Wellbore sheet's LTD vocabulary — types, endpoint lookup, and the request body.
 *
 * An LTD stick is a planned lateral with two UNDESIGNATED ends. One stick becomes a wellbore
 * by designating which end is the surface hole and which the bottom; several become a
 * COMPOSITE — the laterals chained end-to-end by derived connections into one continuous
 * trajectory (U-turns, S-wells).
 *
 * **The chaining itself is the backend's.** This module used to mirror
 * `WellboreGeometry::LTDControlPointProvider` — its Earth radius, its one-mile tie threshold,
 * its tie-break, its perforated sum — so the preview would agree with the save. Two copies of
 * one set of constants is a drift waiting to happen, so the sheet now asks
 * `POST /wellbore_geometry_previews` instead: the same provider the save runs, with nothing
 * persisted. See `use-wellbore-preview.ts`.
 *
 * What is left here is the vocabulary both the request and the UI need, and nothing that has
 * to agree with a number on the backend.
 */

/** Which end of a stick. Matches the ids the LTD candidates API serves. */
export type LtdEndKey = 'end_a' | 'end_b';

export const END_A: LtdEndKey = 'end_a';
export const END_B: LtdEndKey = 'end_b';

/** One end of one stick, as the candidates API serves it. */
export interface LtdEnd {
  id: LtdEndKey;
  label: string;
  lon: number;
  lat: number;
}

/** One LTD stick from `POST /queries/ltd_candidates`. */
export interface LtdCandidate {
  ltdPropertyId: number;
  name: string | null;
  formation: string | null;
  typeCurveScenario: string | null;
  typeCurveArea: string | null;
  unitSurveyArea: string | null;
  state: string | null;
  /** The planned lateral's GEOMETRIC length (ST_Length of the stored line), for display. */
  lateralLengthFt: number | null;
  /**
   * The PERFORATED length — the persisted column the save reports. Shown per row so a reader
   * can reconcile a stick against the preview's total. Null when the stick carries none,
   * which the backend turns into an advisory rather than silently contributing zero.
   */
  perforatedLateralLengthFt: number | null;
  ends: LtdEnd[];
}

/**
 * The opposite end of the same stick. Vocabulary, not geometry.
 * @param {LtdEndKey} end One end.
 * @return {LtdEndKey} The other.
 */
export const otherEnd = (end: LtdEndKey): LtdEndKey => {
  return end === END_A ? END_B : END_A;
};

/**
 * A stick's preferred end reference: the one asked for when the stick serves it, else
 * whichever it does serve.
 *
 * Used for the DEFAULT designations. A stick is not guaranteed to serve both ends (the
 * candidates API omits an end it has no point for), so a default that assumed End A would
 * produce a reference the backend then refuses.
 * @param {LtdCandidate} candidate The stick.
 * @param {LtdEndKey} preferred The end to use when it is served.
 * @return {LtdEndRef | null} The reference, or null when the stick serves no end at all.
 */
export const preferredEndRef = (
  candidate: LtdCandidate,
  preferred: LtdEndKey
): LtdEndRef | null => {
  const ends = candidate.ends ?? [];
  if (ends.length === 0) return null;
  const served = ends.some((end) => end.id === preferred)
    ? preferred
    : ends[0].id;
  return { slotId: candidate.ltdPropertyId, end: served };
};

/** Names one end of one selected stick — what the two designations carry. */
export interface LtdEndRef {
  slotId: number;
  end: LtdEndKey;
}

/** A resolved endpoint: which stick, which end, and where. */
export interface Endpoint {
  slotId: number;
  end: LtdEndKey;
  lon: number;
  lat: number;
}

/**
 * Why the sheet cannot ask for a preview yet.
 *
 * These are INPUT checks, not geometry: the backend refuses the same four in
 * `validate_designations!`, but asking it would cost a round trip to learn something the
 * client already knows. Everything beyond this — whether the sticks actually chain — is the
 * server's answer, and arrives as a 422.
 */
export type ChainRefusal =
  | 'NO_SELECTION'
  | 'SURFACE_NOT_SELECTED'
  | 'BOTTOM_NOT_SELECTED'
  | 'SAME_POINT';

export const REFUSAL_MESSAGES: Record<ChainRefusal, string> = {
  NO_SELECTION: 'Select at least one LTD to build a wellbore from.',
  SURFACE_NOT_SELECTED: 'Designate a surface hole on one of the selected LTDs.',
  BOTTOM_NOT_SELECTED: 'Designate a bottom hole on one of the selected LTDs.',
  SAME_POINT: 'The surface and bottom hole cannot be the same point.',
};

/**
 * Great-circle distance in feet, for HIT-TESTING only.
 *
 * The map uses this to resolve a click to the nearest endpoint within a couple of thousand
 * feet. It is deliberately NOT the measure the geometry is built from — that moved to the
 * backend — so this radius no longer has to agree with any number over there, and a future
 * edit to either cannot put the preview out of step with the save.
 * @param {Endpoint} from One endpoint.
 * @param {Endpoint} to The other.
 * @return {number} Distance in feet.
 */
export const distanceFt = (from: Endpoint, to: Endpoint): number => {
  // Mean Earth radius in feet. A click tolerance is tens of metres of precision at worst.
  const radiusFt = 20_902_231;
  /**
   * @param {number} degrees An angle in degrees.
   * @return {number} It, in radians.
   */
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

  const lat1 = toRadians(from.lat);
  const lat2 = toRadians(to.lat);
  const dLat = lat2 - lat1;
  const dLon = toRadians(to.lon - from.lon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;

  return 2 * radiusFt * Math.asin(Math.sqrt(h));
};

/**
 * One stick's endpoint.
 * @param {LtdCandidate} candidate The stick.
 * @param {LtdEndKey} end Which end.
 * @return {Endpoint | null} The endpoint, or null when the stick does not serve that end.
 */
export const endpointOf = (
  candidate: LtdCandidate,
  end: LtdEndKey
): Endpoint | null => {
  const served = candidate.ends?.find(
    (candidateEnd) => candidateEnd.id === end
  );
  if (!served) return null;

  return {
    slotId: candidate.ltdPropertyId,
    end,
    lon: served.lon,
    lat: served.lat,
  };
};

/** The `wellbore` body, shared verbatim by the preview and the save. */
export interface WellboreBody {
  ltd_slot_ids: number[];
  surface_hole: { slot_id: number; end: LtdEndKey };
  bottom_hole: { slot_id: number; end: LtdEndKey };
}

/**
 * The preview body sent BEFORE a bottom hole is chosen — just the selection and the surface
 * hole. The backend accepts it on the preview only (the save still requires `bottom_hole`)
 * and answers with which bottom end of each stick is valid.
 */
export type WellboreSurfaceBody = Omit<WellboreBody, 'bottom_hole'>;

/** One candidate bottom stick and the single bottom-hole end the chain accepts for it. */
export interface BottomHoleOption {
  slotId: number;
  validEnd: LtdEndKey;
}

/** The backend's INCONSISTENT_BOTTOM_HOLE copy, reused for the greyed-out end. */
export const INCONSISTENT_BOTTOM_HOLE_MESSAGE =
  'The chosen bottom hole is inconsistent with the chained order of the selected LTDs';

/**
 * Whether an end may be designated as the bottom hole.
 *
 * Unknown (options not loaded, or the backend named no entry for the stick) counts as valid:
 * the greying is guidance on top of the backend's own refusal, never a reason to block on a
 * request that has not answered.
 * @param {Map<number, LtdEndKey> | null} validEnds Valid bottom end per stick, or null if unknown.
 * @param {LtdEndRef} ref The end in question.
 * @return {boolean} False only when the backend named a DIFFERENT end for this stick.
 */
export const isBottomEndValid = (
  validEnds: Map<number, LtdEndKey> | null,
  ref: LtdEndRef
): boolean => {
  const valid = validEnds?.get(ref.slotId);
  return valid === undefined || valid === ref.end;
};

/**
 * The ends the bottom hole may be moved to: never one of the SURFACE stick's own.
 *
 * The chain enters at the surface hole and has to leave through a different stick, so the
 * backend never offers the surface stick as a bottom candidate — `#bottom_hole_options` builds
 * its list from the non-surface sticks. It does not send a refusal for it either, and
 * `isBottomEndValid` reads an unnamed stick as valid (deliberately — an unanswered request must
 * not grey anything out), so the surface stick's ends survive that filter and have to be dropped
 * here instead.
 *
 * Shared by the layer that DRAWS the candidates and the click that RESOLVES one, which is the
 * whole point: when only the drawing filtered, a click near the junction could land on the
 * surface stick's far end — chained sticks meet at nearly one coordinate — and silently reverse
 * the bore.
 * @param {Endpoint[]} points Every endpoint of every selected stick.
 * @param {number | null | undefined} surfaceSlotId The stick holding the surface hole.
 * @return {Endpoint[]} The ends a bottom designation may use.
 */
export const bottomPickableEnds = (
  points: Endpoint[],
  surfaceSlotId: number | null | undefined
): Endpoint[] => points.filter((point) => point.slotId !== surfaceSlotId);

/** What `nearestPickableEnd` needs to resolve a click. */
export interface NearestPickableEndInput {
  /** Every endpoint of every selected stick. */
  points: Endpoint[];
  /** The click, as an endpoint-shaped point. */
  click: Endpoint;
  /** The role being picked. */
  role: 'surface' | 'bottom';
  /** Valid bottom end per stick, or null if unknown. */
  validEnds: Map<number, LtdEndKey> | null;
  /** How near the click must land. */
  toleranceFt: number;
}

/**
 * The pickable end nearest a map click, for the role being picked.
 *
 * For the bottom hole the field is narrowed to VALID ends BEFORE taking the nearest: chained
 * sticks meet near one coordinate, so the nearest end overall can be the invalid one while a
 * valid end sits well within tolerance — filtering after would drop a pick the reader made
 * correctly.
 * @param {NearestPickableEndInput} input The endpoints, the click, the role being picked,
 *   the valid bottom ends and the tolerance.
 * @return {LtdEndRef | null} The end to pick, or null when none is within tolerance.
 */
export const nearestPickableEnd = ({
  points,
  click,
  role,
  validEnds,
  toleranceFt,
}: NearestPickableEndInput): LtdEndRef | null => {
  const eligible =
    role === 'bottom'
      ? points.filter((point) =>
          isBottomEndValid(validEnds, { slotId: point.slotId, end: point.end })
        )
      : points;
  if (eligible.length === 0) return null;

  const nearest = eligible.reduce((best, point) => {
    return distanceFt(click, point) < distanceFt(click, best) ? point : best;
  });
  if (distanceFt(click, nearest) > toleranceFt) return null;

  return { slotId: nearest.slotId, end: nearest.end };
};

/**
 * The composite's default bottom hole: a valid end on a non-surface stick.
 *
 * The last-checked stick is the stand-in for "wherever the chain runs out"; once the backend
 * has named the valid ends, a tail it did not name (it reports non-surface sticks only) gives
 * way to one it did, rather than silently assuming `end_b`. With the options unknown the old
 * fallback applies — guidance only, the backend still refuses an inconsistent bore.
 * @param {LtdCandidate[]} selected The checked sticks, in check order (two or more).
 * @param {LtdEndRef} surface The effective surface hole.
 * @param {Map<number, LtdEndKey> | null} validEnds Valid bottom end per stick, or null if unknown.
 * @return {LtdEndRef | null} The default bottom hole, or null when no stick can carry one.
 */
export const defaultBottomEnd = (
  selected: LtdCandidate[],
  surface: LtdEndRef,
  validEnds: Map<number, LtdEndKey> | null
): LtdEndRef | null => {
  const nonSurface = selected.filter(
    (candidate) => candidate.ltdPropertyId !== surface.slotId
  );
  if (nonSurface.length === 0) return null;

  const tail = nonSurface[nonSurface.length - 1];
  if (!validEnds) return preferredEndRef(tail, END_B);

  // Tail first, then back through the others: the first stick the backend named.
  const target = [...nonSurface]
    .reverse()
    .find((candidate) => validEnds.has(candidate.ltdPropertyId));
  if (!target) return preferredEndRef(tail, END_B);

  return preferredEndRef(target, validEnds.get(target.ltdPropertyId)!);
};

/**
 * Build the request body.
 *
 * ONE builder, used for both the preview and the save — the backend permits them through the
 * same `LTDCompositeProviderParams`, so sending the same object is what guarantees the shape
 * the sheet draws is the shape that gets built. Two builders would reintroduce exactly the
 * drift that retiring the client-side chain model removed.
 *
 * Sends the checked sticks as the reader checked them. The backend collapses an untraversed
 * selection itself (both holes on one stick drops the rest) and echoes what it used back as
 * `orderedMembers`, so there is nothing to pre-filter here.
 * @param {number[]} selectedIds The checked stick ids.
 * @param {LtdEndRef} surface The designated surface hole.
 * @param {LtdEndRef} bottom The designated bottom hole.
 * @return {WellboreBody} The body, snake_cased as both endpoints take it.
 */
export const toWellboreBody = (
  selectedIds: number[],
  surface: LtdEndRef,
  bottom: LtdEndRef
): WellboreBody => ({
  ltd_slot_ids: [...new Set(selectedIds)],
  surface_hole: { slot_id: surface.slotId, end: surface.end },
  bottom_hole: { slot_id: bottom.slotId, end: bottom.end },
});

/**
 * The surface-only body for the bottom-hole options request.
 * @param {number[]} selectedIds The checked stick ids.
 * @param {LtdEndRef} surface The designated surface hole.
 * @return {WellboreSurfaceBody} The body, snake_cased as the endpoint takes it.
 */
export const toSurfaceBody = (
  selectedIds: number[],
  surface: LtdEndRef
): WellboreSurfaceBody => ({
  ltd_slot_ids: [...new Set(selectedIds)],
  surface_hole: { slot_id: surface.slotId, end: surface.end },
});

/**
 * Whether the current inputs are worth sending.
 *
 * Mirrors the backend's own `validate_designations!` so the button and its refusal copy are
 * instant rather than trailing a debounce — but it answers only about the INPUTS. Whether the
 * sticks chain into a bore is the server's call.
 * @param {number[]} selectedIds The checked stick ids.
 * @param {LtdEndRef | null} surface The designated surface hole.
 * @param {LtdEndRef | null} bottom The designated bottom hole.
 * @return {ChainRefusal | null} The reason it is not sendable, or null when it is.
 */
export const designationRefusal = (
  selectedIds: number[],
  surface: LtdEndRef | null,
  bottom: LtdEndRef | null
): ChainRefusal | null => {
  if (selectedIds.length === 0) return 'NO_SELECTION';
  if (!surface || !selectedIds.includes(surface.slotId)) {
    return 'SURFACE_NOT_SELECTED';
  }
  if (!bottom || !selectedIds.includes(bottom.slotId)) {
    return 'BOTTOM_NOT_SELECTED';
  }
  // A bore from one point to itself has no length; the backend refuses it too.
  if (surface.slotId === bottom.slotId && surface.end === bottom.end) {
    return 'SAME_POINT';
  }

  return null;
};
